import React, { useState, useEffect, useMemo, useRef } from "react";
import { Form, Input, Select, Button, Modal, Switch, Divider, message } from "antd";
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, EyeOutlined } from "@ant-design/icons";
import TemplatePreview from "./TemplatePreview";
import { postAuthMethod } from "../../../services/HttpServices";
import MediaPickerModal from "../MediaPickerModal";
import { useAlert } from "../../../context/AlertProvider";

const { Option } = Select;
const { TextArea } = Input;

const WhatsAppTemplateComposer = ({ visible, open, onCancel, onClose, onSuccess }) => {
  const alertContext = useAlert();
  const showAlert = alertContext?.showAlert;
  const fileInputRef = useRef(null);
  const [templateName, setTemplateName] = useState("");
  const [category, setCategory] = useState("MARKETING");
  const [language, setLanguage] = useState("en_US");
  
  const [hasHeader, setHasHeader] = useState(false);
  const [headerFormat, setHeaderFormat] = useState("TEXT");
  const [headerText, setHeaderText] = useState("");
  const [headerSample, setHeaderSample] = useState("");
  const [isMediaPickerVisible, setIsMediaPickerVisible] = useState(false);
  const [selectedMediaObj, setSelectedMediaObj] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  
  const [bodyText, setBodyText] = useState("");
  const [bodySamples, setBodySamples] = useState({});
  
  const [hasFooter, setHasFooter] = useState(false);
  const [footerText, setFooterText] = useState("");
  
  const [buttons, setButtons] = useState([]); // { id, type: 'URL' | 'QUICK_REPLY', text: '', url: '' }
  const [urlSample, setUrlSample] = useState({});
  
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isModalOpen = Boolean(open || visible);

  // Reset form & errors on modal close
  useEffect(() => {
    if (!isModalOpen) {
      setErrors({});
      setTemplateName("");
      setCategory("MARKETING");
      setLanguage("en_US");
      setHasHeader(false);
      setHeaderFormat("TEXT");
      setHeaderText("");
      setHeaderSample("");
      setSelectedMediaObj(null);
      setBodyText("");
      setBodySamples({});
      setHasFooter(false);
      setFooterText("");
      setButtons([]);
      setUrlSample({});
    }
  }, [isModalOpen]);

  // Extract variables from string: "{{1}}", "{{2}}" -> ["1", "2"]
  const extractVariables = (text) => {
    if (!text) return [];
    const matches = text.match(/\{\{(\d+)\}\}/g);
    if (!matches) return [];
    return [...new Set(matches.map(m => m.replace(/[{}]/g, "")))].sort((a, b) => parseInt(a) - parseInt(b));
  };

  const headerVars = useMemo(() => extractVariables(headerText), [headerText]);
  const bodyVars = useMemo(() => extractVariables(bodyText), [bodyText]);

  const handleBodySampleChange = (index, value) => {
    setBodySamples(prev => ({ ...prev, [index]: value }));
    if (errors.bodySamples) {
      setErrors(prev => ({ ...prev, bodySamples: null }));
    }
  };

  const handleDirectFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const format = headerFormat.toUpperCase();
    if (format === "IMAGE" && !file.type.startsWith("image/")) {
      const isImageExt = /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(file.name);
      if (!isImageExt) {
        const msg = "Please select a valid image file (JPG, PNG, WEBP, etc.)";
        if (showAlert) showAlert("error", msg);
        else message.error(msg);
        return;
      }
    } else if (format === "VIDEO" && !file.type.startsWith("video/")) {
      const isVideoExt = /\.(mp4|3gp|mov|avi|mkv|webm)$/i.test(file.name);
      if (!isVideoExt) {
        const msg = "Please select a valid video file (MP4, 3GP, etc.)";
        if (showAlert) showAlert("error", msg);
        else message.error(msg);
        return;
      }
    } else if (format === "DOCUMENT") {
      if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
        const msg = "Please select a document file (PDF, DOC, TXT, etc.)";
        if (showAlert) showAlert("error", msg);
        else message.error(msg);
        return;
      }
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploadingMedia(true);
    try {
      const res = await postAuthMethod("/api/media", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (showAlert) {
        showAlert("success", "Media uploaded successfully");
      } else {
        message.success("Media uploaded successfully");
      }

      if (res.data) {
        const localPreviewUrl =
          file.type.startsWith("image/") || file.type.startsWith("video/")
            ? URL.createObjectURL(file)
            : null;

        const mediaItem = {
          ...(typeof res.data === "object" ? res.data : { id: res.data }),
          name: file.name,
          fileName: file.name,
          previewUrl:
            localPreviewUrl ||
            res.data?.mediaUrl ||
            res.data?.fileUrl ||
            res.data?.url,
        };
        setSelectedMediaObj(mediaItem);
        if (errors?.headerMedia) {
          setErrors((prev) => ({ ...prev, headerMedia: null }));
        }
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || "File upload failed";
      if (showAlert) {
        showAlert("error", errMsg);
      } else {
        message.error(errMsg);
      }
    } finally {
      setUploadingMedia(false);
    }
  };

  const buildComponents = () => {
    const components = [];
    
    // Header
    if (hasHeader) {
      if (headerFormat === "TEXT" && headerText) {
        const headerComp = { type: "HEADER", format: "TEXT", text: headerText };
        if (headerVars.length > 0 && headerSample) {
          headerComp.example = { header_text: [headerSample] };
        }
        components.push(headerComp);
      } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat) && selectedMediaObj) {
        components.push({
          type: "HEADER",
          format: headerFormat,
          example: { header_handle: [selectedMediaObj.id.toString()] }
        });
      }
    }
    
    // Body
    if (bodyText) {
      const bodyComp = { type: "BODY", text: bodyText };
      if (bodyVars.length > 0) {
        const samples = bodyVars.map(v => bodySamples[v] || `sample_${v}`);
        bodyComp.example = { body_text: [samples] };
      }
      components.push(bodyComp);
    }
    
    // Footer
    if (hasFooter && footerText) {
      components.push({ type: "FOOTER", text: footerText });
    }
    
    // Buttons
    if (buttons.length > 0) {
      const buttonComps = buttons.map(b => {
        if (b.type === "QUICK_REPLY") {
          return { type: "QUICK_REPLY", text: b.text };
        }
        if (b.type === "URL") {
          const comp = { type: "URL", text: b.text, url: b.url };
          if (b.url && b.url.includes("{{1}}") && urlSample[b.id]) {
            comp.example = [urlSample[b.id]];
          }
          return comp;
        }
        return null;
      }).filter(Boolean);

      if (buttonComps.length > 0) {
        components.push({ type: "BUTTONS", buttons: buttonComps });
      }
    }
    
    return components;
  };

  const autoFixVariableOrder = (text) => {
    if (!text) return text;
    let counter = 1;
    const map = {};
    return text.replace(/\{\{(\d+)\}\}/g, (match, p1) => {
      if (!map[p1]) {
        map[p1] = counter++;
      }
      return `{{${map[p1]}}}`;
    });
  };

  const validateForm = () => {
    const errs = {};

    // 1. Template Name
    if (!templateName || !templateName.trim()) {
      errs.templateName = "Template name is required.";
    } else if (!/^[a-z0-9_]+$/.test(templateName.trim())) {
      errs.templateName = "Lowercase alphanumeric characters and underscores only.";
    }

    // 2. Language
    if (!language || !language.trim()) {
      errs.language = "Language code is required.";
    }

    // 3. Body
    if (!bodyText || !bodyText.trim()) {
      errs.bodyText = "Message body text is required.";
    } else {
      if (bodyText.includes("{{0}}")) {
        errs.bodyText = "Meta requires variables to start at {{1}} ({{0}} is not permitted).";
      } else if (bodyVars.length > 0) {
        const isSequential = bodyVars.every((v, idx) => parseInt(v, 10) === idx + 1);
        if (!isSequential) {
          errs.bodyText = `Variables must be numbered sequentially starting from {{1}} (found: ${bodyVars.map(v => `{{${v}}}`).join(", ")}).`;
        }
      }
    }

    // 4. Header (optional)
    if (hasHeader) {
      if (headerFormat === "TEXT") {
        if (!headerText || !headerText.trim()) {
          errs.headerText = "Header text is required when header is enabled.";
        } else if (headerText.includes("{{0}}")) {
          errs.headerText = "Header variable must start at {{1}} ({{0}} is not permitted).";
        } else if (headerVars.length > 0) {
          if (headerVars[0] !== "1") {
            errs.headerText = "Header variable must be {{1}}.";
          }
        }
      } else {
        if (!selectedMediaObj) {
          errs.headerMedia = `Media selection is required for ${headerFormat} header.`;
        }
      }
    }

    // 5. Buttons (optional)
    if (buttons.length > 0) {
      const btnErrs = {};
      buttons.forEach((btn) => {
        if (!btn.text || !btn.text.trim()) {
          btnErrs[`btn_${btn.id}_text`] = "Button text is required.";
        }
        if (btn.type === "URL") {
          if (!btn.url || !btn.url.trim()) {
            btnErrs[`btn_${btn.id}_url`] = "URL is required for Visit Website button.";
          } else if (btn.url.includes("{{1}}") && (!urlSample[btn.id] || !urlSample[btn.id].trim())) {
            btnErrs[`btn_${btn.id}_urlSample`] = "Sample URL parameter value is required.";
          }
        }
      });
      if (Object.keys(btnErrs).length > 0) {
        errs.buttons = btnErrs;
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      if (showAlert) {
        showAlert("error", "Please fill in all required fields marked in red.");
      } else {
        message.error("Please fill in all required fields marked in red.");
      }
      return;
    }

    const payload = {
      name: templateName.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_"),
      category,
      language: language.trim(),
      components: buildComponents(),
      defaultMediaAssetId: selectedMediaObj ? selectedMediaObj.id : null
    };

    setSubmitting(true);
    try {
      const res = await postAuthMethod("/api/whatsapp/templates", payload);
      if (showAlert) {
        showAlert("success", "Template created and submitted to Meta successfully!");
      } else {
        message.success("Template created and submitted to Meta successfully!");
      }
      if (onSuccess) onSuccess(res.data);
      if (onCancel) onCancel();
      if (onClose) onClose();
    } catch (err) {
      console.error("Error creating template", err);
      const errMsg = err.response?.data?.message || err.message || "Failed to create template.";
      if (showAlert) {
        showAlert("error", errMsg);
      } else {
        message.error(errMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const currentComponents = buildComponents();
  const sampleValuesForPreview = {
    header: headerSample,
    body: bodySamples
  };

  const handleClose = onCancel || onClose;

  return (
    <Modal
      title={<span className="tw-text-slate-800 tw-text-lg tw-font-bold">Create WhatsApp Template</span>}
      width={1020}
      open={isModalOpen}
      visible={isModalOpen}
      onCancel={handleClose}
      destroyOnClose
      centered
      style={{ top: 20 }}
      styles={{
        content: { borderRadius: "16px", overflow: "hidden", padding: 0 },
        header: { background: "#F8FAFC", padding: "16px 24px", margin: 0, borderBottom: "1px solid #F1F5F9" },
        body: { padding: "20px 24px", maxHeight: "78vh", overflow: "hidden" },
        footer: { background: "#FFFFFF", padding: "14px 24px", margin: 0, borderTop: "1px solid #F1F5F9", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px" }
      }}
      footer={[
        <Button
          key="cancel"
          onClick={handleClose}
          className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={submitting}
          className="!tw-h-[38px] !tw-rounded-lg !tw-bg-[#5D44FF] !tw-border-[#5D44FF] !tw-text-white tw-text-xs tw-font-bold tw-px-6 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
        >
          Submit to Meta
        </Button>
      ]}
    >
      <div className="tw-max-w-7xl tw-mx-auto tw-flex tw-gap-6 tw-h-[72vh] tw-max-h-[680px] tw-overflow-hidden">
        {/* Left Panel: Form Controls */}
        <div className="tw-flex-1 tw-overflow-y-auto sleek-scrollbar tw-pr-3.5 tw-h-full">
          <Form layout="vertical">
            <div className="tw-bg-gray-50 tw-p-5 tw-rounded-lg tw-mb-6">
              <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                {/* Template Name */}
                <div className="tw-flex tw-flex-col">
                  <label className="tw-text-xs tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wider tw-mb-1.5 tw-flex tw-items-center tw-gap-1">
                    Template Name <span className="tw-text-rose-500">*</span>
                  </label>
                  <Input 
                    placeholder="e.g. seasonal_promotion" 
                    value={templateName}
                    status={errors?.templateName ? "error" : ""}
                    onChange={(e) => {
                      const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_");
                      setTemplateName(val);
                      if (errors?.templateName) {
                        setErrors(prev => ({ ...prev, templateName: null }));
                      }
                    }}
                    className={`!tw-h-[42px] !tw-rounded-xl ${errors?.templateName ? '!tw-border-rose-500' : ''}`}
                  />
                  <span className="tw-text-xs tw-text-gray-500 tw-mt-1">Lowercase alphanumeric and underscores only.</span>
                  {errors?.templateName && (
                    <div className="tw-text-xs tw-text-rose-500 tw-font-medium tw-mt-1">
                      {errors.templateName}
                    </div>
                  )}
                </div>

                {/* Category */}
                <div className="tw-flex tw-flex-col">
                  <label className="tw-text-xs tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wider tw-mb-1.5 tw-flex tw-items-center tw-gap-1">
                    Category <span className="tw-text-rose-500">*</span>
                  </label>
                  <Select value={category} onChange={setCategory} className="tw-h-[42px]">
                    <Option value="MARKETING">Marketing</Option>
                    <Option value="UTILITY">Utility</Option>
                    <Option value="AUTHENTICATION">Authentication</Option>
                  </Select>
                </div>

                {/* Language */}
                <div className="tw-flex tw-flex-col">
                  <label className="tw-text-xs tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wider tw-mb-1.5 tw-flex tw-items-center tw-gap-1">
                    Language <span className="tw-text-rose-500">*</span>
                  </label>
                  <Input 
                    value={language} 
                    status={errors?.language ? "error" : ""}
                    onChange={e => {
                      setLanguage(e.target.value);
                      if (errors?.language) setErrors(prev => ({ ...prev, language: null }));
                    }} 
                    className={`!tw-h-[42px] !tw-rounded-xl ${errors?.language ? '!tw-border-rose-500' : ''}`}
                  />
                  {errors?.language && (
                    <div className="tw-text-xs tw-text-rose-500 tw-font-medium tw-mt-1">
                      {errors.language}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Divider>Message Content</Divider>

            {/* Header */}
            <div className="tw-mb-6">
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                <div className="tw-font-medium">Header <span className="tw-text-gray-400 tw-font-normal">(Optional)</span></div>
                <Switch checked={hasHeader} onChange={setHasHeader} size="small" />
              </div>
              {hasHeader && (
                <div className="tw-mt-2">
                  <Select 
                    value={headerFormat} 
                    onChange={(val) => {
                      setHeaderFormat(val);
                      setSelectedMediaObj(null);
                      if (errors?.headerMedia) {
                        setErrors(prev => ({ ...prev, headerMedia: null }));
                      }
                    }} 
                    className="tw-w-full tw-mb-3 !tw-h-[42px]"
                  >
                    <Option value="TEXT">Text</Option>
                    <Option value="IMAGE">Image</Option>
                    <Option value="VIDEO">Video</Option>
                    <Option value="DOCUMENT">Document</Option>
                  </Select>
                  
                  {headerFormat === "TEXT" && (
                    <>
                      <Input 
                        placeholder="Enter header text... (Supports {{1}})" 
                        value={headerText}
                        status={errors?.headerText ? "error" : ""}
                        onChange={e => {
                          setHeaderText(e.target.value);
                          if (errors?.headerText) setErrors(prev => ({ ...prev, headerText: null }));
                        }}
                        maxLength={60}
                        showCount
                        className={`!tw-h-[42px] !tw-rounded-xl ${errors?.headerText ? '!tw-border-rose-500' : ''}`}
                      />
                      {errors?.headerText && (
                        <div className="tw-mt-1.5 tw-text-xs tw-text-rose-500 tw-font-medium">
                          {errors.headerText}
                        </div>
                      )}
                      {headerVars.length > 0 && (
                        <div className="tw-mt-3 tw-p-3.5 tw-bg-[#F4F7FF] tw-rounded-xl tw-border tw-border-solid tw-border-[#E2E8F0]">
                          <div className="tw-text-xs tw-font-bold tw-text-slate-800 tw-mb-1 tw-flex tw-items-center tw-gap-1.5">
                            <EyeOutlined className="tw-text-blue-600" /> Header Variable Preview Value
                          </div>
                          <p className="tw-text-[11.5px] tw-text-slate-600 tw-mb-2.5 tw-leading-relaxed">
                            Enter a sample value for header variable <code className="tw-text-blue-600 tw-bg-blue-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10.5px] tw-font-bold">{"{{1}}"}</code> to see how it renders in the live preview.
                          </p>
                          <Input 
                            size="small" 
                            placeholder="Preview value for {{1}}" 
                            value={headerSample} 
                            onChange={e => setHeaderSample(e.target.value)} 
                            className="!tw-rounded-lg !tw-h-8 !tw-bg-white"
                          />
                        </div>
                      )}
                    </>
                  )}
                  
                  {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat) && (
                    <div className="tw-p-5 tw-border tw-border-dashed tw-border-gray-300 tw-rounded-xl tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-gray-50/50">
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept={
                          headerFormat === "IMAGE"
                            ? "image/*,image/jpeg,image/png,image/jpg,image/webp,image/gif"
                            : headerFormat === "VIDEO"
                            ? "video/*,video/mp4,video/3gpp,video/quicktime,video/avi"
                            : ".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                        }
                        onChange={handleDirectFileSelect}
                      />
                      
                      <Button 
                        type="primary"
                        loading={uploadingMedia}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        className={`!tw-bg-[#5D44FF] hover:!tw-bg-[#4B34E6] !tw-text-white !tw-border-none !tw-rounded-full !tw-h-10 !tw-px-6 !tw-font-semibold tw-shadow-sm ${errors?.headerMedia ? '!tw-border-rose-500 !tw-text-rose-500' : ''}`}
                      >
                        {selectedMediaObj ? "Change Media" : "Select Media"}
                      </Button>
                      
                      {selectedMediaObj && (
                        <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-px-3.5 tw-py-2 tw-bg-emerald-50 tw-border tw-border-solid tw-border-emerald-200 tw-rounded-xl tw-text-xs tw-text-emerald-700 tw-font-medium">
                          <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-emerald-500"></div>
                          <span className="tw-truncate tw-max-w-[260px]">
                            {selectedMediaObj.fileName || selectedMediaObj.name || "Media Selected"}
                          </span>
                        </div>
                      )}
                      
                      {errors?.headerMedia && (
                        <div className="tw-mt-2 tw-text-xs tw-text-rose-500 tw-font-medium">
                          {errors.headerMedia}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Body */}
            <div className="tw-mb-6">
              <div className="tw-font-bold tw-text-sm tw-text-slate-800 tw-mb-2 tw-flex tw-items-center tw-gap-1">
                Body <span className="tw-text-rose-500">*</span>
              </div>
              <TextArea 
                rows={5} 
                placeholder="Enter message body... Use {{1}}, {{2}} for variables"
                value={bodyText}
                status={errors?.bodyText ? "error" : ""}
                onChange={e => {
                  setBodyText(e.target.value);
                  if (errors?.bodyText) setErrors(prev => ({ ...prev, bodyText: null }));
                }}
                maxLength={1024}
                showCount
                className={`!tw-rounded-xl ${errors?.bodyText ? '!tw-border-rose-500' : ''}`}
              />
              {errors?.bodyText && (
                <div className="tw-mt-1.5 tw-flex tw-items-center tw-justify-between tw-gap-2">
                  <div className="tw-text-xs tw-text-rose-500 tw-font-medium">
                    {errors.bodyText}
                  </div>
                  {(bodyText.includes("{{0}}") || (bodyVars.length > 0 && !bodyVars.every((v, idx) => parseInt(v, 10) === idx + 1))) && (
                    <button
                      type="button"
                      onClick={() => {
                        const fixed = autoFixVariableOrder(bodyText);
                        setBodyText(fixed);
                        setErrors(prev => ({ ...prev, bodyText: null }));
                      }}
                      className="tw-text-[11px] tw-font-bold tw-text-blue-600 hover:tw-text-blue-700 tw-bg-blue-50 hover:tw-bg-blue-100 tw-px-2.5 tw-py-1 tw-rounded-lg tw-border tw-border-solid tw-border-blue-200 tw-cursor-pointer tw-transition-all tw-shrink-0"
                    >
                      ⚡ Auto-Fix Variable Order
                    </button>
                  )}
                </div>
              )}
              {bodyVars.length > 0 && (
                <div className="tw-mt-3.5 tw-p-4 tw-bg-[#F4F7FF] tw-rounded-xl tw-border tw-border-solid tw-border-[#E2E8F0]">
                  <div className="tw-flex tw-items-start tw-gap-2.5 tw-mb-3">
                    <EyeOutlined className="tw-text-blue-600 tw-text-base tw-mt-0.5 tw-flex-shrink-0" />
                    <div>
                      <div className="tw-text-xs tw-font-bold tw-text-slate-800">
                        Preview Values for Variables
                      </div>
                      <p className="tw-text-[11.5px] tw-text-slate-600 tw-mt-0.5 tw-mb-0 tw-leading-relaxed">
                        Enter sample values below to test and preview how dynamic variables <code className="tw-text-blue-600 tw-bg-blue-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10.5px] tw-font-bold">{"{{1}}"}</code>, <code className="tw-text-blue-600 tw-bg-blue-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10.5px] tw-font-bold">{"{{2}}"}</code> will appear in the live mobile phone preview on the right.
                      </p>
                    </div>
                  </div>

                  <div className="tw-mt-3">
                    <div className="tw-text-[11px] tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wider tw-mb-2">
                      Enter Preview Values:
                    </div>
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                      {bodyVars.map(v => (
                        <div key={v} className="tw-flex tw-flex-col tw-gap-1">
                          <label className="tw-text-[11px] tw-font-medium tw-text-slate-600">
                            Preview for <code className="tw-text-blue-600 tw-bg-blue-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10.5px] tw-font-bold">{"{{" + v + "}}"}</code>
                          </label>
                          <Input 
                            size="small"
                            placeholder={`Sample value for {{${v}}}`}
                            value={bodySamples[v] || ''}
                            onChange={e => handleBodySampleChange(v, e.target.value)}
                            className="!tw-rounded-lg !tw-h-9 !tw-bg-white"
                          />
                        </div>
                      ))}
                    </div>
                    {errors?.bodySamples && (
                      <div className="tw-mt-2 tw-text-xs tw-text-rose-500 tw-font-medium">
                        {errors.bodySamples}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="tw-mb-6">
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                <div className="tw-font-medium">Footer <span className="tw-text-gray-400 tw-font-normal">(Optional)</span></div>
                <Switch checked={hasFooter} onChange={setHasFooter} size="small" />
              </div>
              {hasFooter && (
                <div className="tw-mt-2">
                  <Input 
                    placeholder="Enter short footer text..." 
                    value={footerText}
                    onChange={e => setFooterText(e.target.value)}
                    maxLength={60}
                    showCount
                    className="!tw-h-[42px] !tw-rounded-xl"
                  />
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="tw-mb-6">
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                <div className="tw-font-medium">Buttons <span className="tw-text-gray-400 tw-font-normal">(Optional)</span></div>
                <Button 
                  type="dashed" 
                  size="small" 
                  icon={<PlusOutlined />} 
                  onClick={() => setButtons([...buttons, { id: Date.now(), type: 'QUICK_REPLY', text: '' }])}
                  disabled={buttons.length >= 3}
                >
                  Add Button
                </Button>
              </div>
              
              {buttons.map((btn, idx) => (
                <div key={btn.id} className="tw-mb-3 tw-flex tw-flex-col tw-gap-2">
                  <div className="tw-flex tw-gap-2 tw-items-start">
                    <Select 
                      value={btn.type} 
                      onChange={val => {
                        const newBtns = [...buttons];
                        newBtns[idx].type = val;
                        setButtons(newBtns);
                      }}
                      className="tw-w-[140px] tw-flex-shrink-0 [&_.ant-select-selector]:!tw-h-[42px] [&_.ant-select-selector]:!tw-rounded-xl [&_.ant-select-selector]:!tw-flex [&_.ant-select-selector]:!tw-items-center [&_.ant-select-selection-item]:!tw-flex [&_.ant-select-selection-item]:!tw-items-center [&_.ant-select-selection-item]:!tw-leading-none"
                    >
                      <Option value="QUICK_REPLY">Quick Reply</Option>
                      <Option value="URL">Visit Website</Option>
                    </Select>
                    <div className="tw-flex-1 tw-flex tw-flex-col">
                      <Input 
                        placeholder={
                          btn.type === "URL"
                            ? "e.g. Visit Website, Claim Offer"
                            : "e.g. Stop Promotions, Book Call, Yes"
                        } 
                        value={btn.text}
                        status={errors?.buttons?.[`btn_${btn.id}_text`] ? "error" : ""}
                        onChange={e => {
                          const newBtns = [...buttons];
                          newBtns[idx].text = e.target.value;
                          setButtons(newBtns);
                          if (errors?.buttons) setErrors(prev => ({ ...prev, buttons: null }));
                        }}
                        maxLength={25}
                        className="!tw-h-[42px] !tw-rounded-xl"
                      />
                      {errors?.buttons?.[`btn_${btn.id}_text`] && (
                        <div className="tw-mt-1 tw-text-xs tw-text-rose-500 tw-font-medium">
                          {errors.buttons[`btn_${btn.id}_text`]}
                        </div>
                      )}
                    </div>
                    <Button 
                      type="text" 
                      danger 
                      icon={<DeleteOutlined />} 
                      onClick={() => setButtons(buttons.filter((_, i) => i !== idx))} 
                      className="!tw-h-[42px] !tw-w-[42px] !tw-flex !tw-items-center !tw-justify-center !tw-rounded-xl tw-flex-shrink-0 hover:!tw-bg-rose-50"
                    />
                  </div>
                  {btn.type === "URL" && (
                    <div className="tw-w-full tw-mt-1">
                      <Input 
                        placeholder="https://example.com/pricing or https://example.com/offer/{{1}}"
                        value={btn.url}
                        status={errors?.buttons?.[`btn_${btn.id}_url`] ? "error" : ""}
                        onChange={e => {
                          const newBtns = [...buttons];
                          newBtns[idx].url = e.target.value;
                          setButtons(newBtns);
                          if (errors?.buttons) setErrors(prev => ({ ...prev, buttons: null }));
                        }}
                        className="!tw-h-[38px] !tw-rounded-xl"
                      />
                      {errors?.buttons?.[`btn_${btn.id}_url`] && (
                        <div className="tw-mt-1 tw-text-xs tw-text-rose-500 tw-font-medium">
                          {errors.buttons[`btn_${btn.id}_url`]}
                        </div>
                      )}
                      
                      {/* Helpful Info Box */}
                      <div className="tw-mt-2 tw-p-3 tw-bg-blue-50/80 tw-rounded-xl tw-border tw-border-solid tw-border-blue-100 tw-text-[11.5px] tw-text-slate-600 tw-flex tw-items-start tw-gap-2">
                        <InfoCircleOutlined className="tw-text-blue-600 tw-mt-0.5 tw-flex-shrink-0" />
                        <div className="tw-space-y-1">
                          <div className="tw-font-semibold tw-text-slate-700">
                            Valid WhatsApp Website URL Examples:
                          </div>
                          <div>
                            • <strong>Static URL:</strong> <span className="tw-font-mono tw-text-slate-800">https://example.com/pricing</span>
                          </div>
                          <div>
                            • <strong>Dynamic URL (Optional):</strong> <span className="tw-font-mono tw-text-slate-800">https://example.com/offer/{"{{1}}"}</span>
                          </div>
                          <div className="tw-text-[10.5px] tw-text-slate-500 tw-mt-1">
                            Note: URLs must start with <code className="tw-bg-white tw-px-1 tw-py-0.5 tw-rounded tw-text-blue-600">https://</code> or <code className="tw-bg-white tw-px-1 tw-py-0.5 tw-rounded tw-text-blue-600">http://</code>. Variables like <code className="tw-bg-white tw-px-1 tw-py-0.5 tw-rounded tw-text-blue-600">{"{{1}}"}</code> are optional.
                          </div>
                        </div>
                      </div>

                      {btn.url && btn.url.includes("{{1}}") && (
                        <div className="tw-mt-2.5 tw-p-3 tw-bg-slate-50 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-text-xs">
                          <div className="tw-font-semibold tw-text-slate-700 tw-mb-1.5 tw-flex tw-items-center tw-gap-1.5">
                            <EyeOutlined className="tw-text-blue-600" /> Sample value for <code className="tw-text-blue-600">{"{{1}}"}</code>:
                          </div>
                          <Input 
                            placeholder="e.g. promo123 or discount-code" 
                            value={urlSample[btn.id] || ''} 
                            onChange={e => setUrlSample({...urlSample, [btn.id]: e.target.value})} 
                            className="!tw-rounded-lg !tw-h-9 !tw-bg-white" 
                          />
                          {errors?.buttons?.[`btn_${btn.id}_urlSample`] && (
                            <div className="tw-mt-1 tw-text-xs tw-text-rose-500 tw-font-medium">
                              {errors.buttons[`btn_${btn.id}_urlSample`]}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

          </Form>
        </div>

        {/* Right Panel: Live Preview */}
        <div className="tw-w-[370px] tw-flex-shrink-0 tw-flex tw-justify-center tw-bg-gray-50 tw-p-5 tw-rounded-2xl tw-border tw-border-solid tw-border-gray-200 tw-overflow-y-auto sleek-scrollbar tw-h-full">
          <TemplatePreview
            components={currentComponents}
            sampleValues={sampleValuesForPreview}
            mediaObj={selectedMediaObj}
          />
        </div>
      </div>
      
      <MediaPickerModal 
        visible={isMediaPickerVisible}
        onClose={() => setIsMediaPickerVisible(false)}
        onSelect={setSelectedMediaObj}
        requiredType={['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat) ? headerFormat : null}
      />
    </Modal>
  );
};

export default WhatsAppTemplateComposer;
